// Calc Data
import {
    chipsMap,
    ChipData,
    payoutsCollection,
    firstPayoutObj
} from './calculator-data';
import {
    payoutStructuresEnum,
    blindLevelsEnum,
    numberOfChipsEnum,
    tournamentLengthsEnum,
} from '../config/form';

/**
 * Returns one chip distribution collection from chipsMap,
 * where numberOfChips value is a Map Key 
 * @param {Number} numberOfChips 
 * @param {Number} numberOfPlayers
 * @returns {Array.<ChipData>} - Collection of chips data 
 * (color, value and quantity multiply by numberOfPlayers)
 */
const getChipDistribution = (numberOfChips, numberOfPlayers) => {
    const currentChipsData = chipsMap.get(numberOfChips) || [];

    return currentChipsData.map(
        ({ quantity, ...restChipData }) => (
            {
                quantity: quantity * numberOfPlayers,
                eachPlayer: quantity,
                ...restChipData
            }
        )
    );
};

/**
 * Returns prize pool and array of tournament payouts
 * @param {Number} buyIn 
 * @param {Number} numberOfPlayers 
 * @param {payoutStructuresEnum} payoutStructure
 * @returns {Object.<Number, Array.<Number>>} - Object contains
 * full prize poll and Array of payouts based on payoutStructure value
 */
const getTournamentPayouts = (buyIn, numberOfPlayers, payoutStructure) => {
    const prizePool = buyIn * numberOfPlayers;

    // Payouts help methods
    const payoutInRange = ({ rangeOfPlayers }) => rangeOfPlayers.min <= numberOfPlayers && rangeOfPlayers.max >= numberOfPlayers;
    const countPrizePoolPercentage = (percentage) => (prizePool * percentage) / 100;

    const structureOptions = {
        regular: payoutsCollection.find(payoutInRange).prizePoolPercentages,
        top3: firstPayoutObj.prizePoolPercentages,
        winner: [100]
    }

    const payoutValues = structureOptions[payoutStructure].map(countPrizePoolPercentage);
    return { prizePool, payoutValues };
}

/**
 * Returns blind structure with blind values
 * and time based on blindLevel and tournamentLength  
 * @param {blindLevelsEnum} blindLevel 
 * @param {numberOfChipsEnum} numberOfChips 
 * @param {tournamentLengthsEnum} tournamentLength 
 * @param {tournamentTypesEnum} tournamentType 
 * @returns { Array.< Object.<Number, Number, Number> > } - collection of
 * blind object, which contains small and big blind value and time (in minutes) 
 */
const getBlindStructure = (
    blindLevel,
    numberOfChips,
    tournamentLength,
    tournamentType
) => {
    const currentChipsData = chipsMap.get(numberOfChips);
    const firstChipData = currentChipsData[0];
    const startingSmallBlind = firstChipData.value;

    const tournamentTypeBlindFns = {
        regular: (stepIndex) => startingSmallBlind * (stepIndex + 1),
        turbo: (stepIndex) => startingSmallBlind * (stepIndex + 1) + startingSmallBlind * (stepIndex - 1),
        superturbo: (stepIndex) => startingSmallBlind * 2 ** stepIndex
    }
    const blindFn = tournamentTypeBlindFns.regular;

    const structureSteps = Math.ceil(tournamentLength * 60 / blindLevel);
    return [...Array(structureSteps).keys()].map(
        stepIndex => {
            const smallBlind = stepIndex ? blindFn(stepIndex) : tournamentTypeBlindFns.regular(stepIndex);
            const bigBlind = smallBlind * 2;
            const time = blindLevel * (stepIndex + 1);
            return { smallBlind, bigBlind, time }
        }
    )
}

export {
    getChipDistribution,
    getTournamentPayouts,
    getBlindStructure
}